<template>
    <modal :show="show" max-width="3xl" @close="close">
        <div class="px-6 py-4 bg-minplan-beige">
            <page-title :title="$t('templateTransfer.title')"></page-title>
            <p class="text-xs text-gray-500 mt-1">
                {{ $t("templateTransfer.step", { step }) }}:
                {{ stepLabel }}
            </p>
        </div>

        <div class="p-6" v-if="loading">
            <p class="text-sm text-gray-500">{{ $t("templateTransfer.loading") }}</p>
        </div>

        <p v-else-if="error" class="p-6 text-sm text-red-600">
            {{ $t("errors.unexpected") }}
        </p>

        <!-- Schritt 1: Vorschau über alle offenen Wohnungen. Wo übertragen wird, entscheidet
             der Nutzer direkt hier (je Änderung, je Gebäude, je Ausnahme). -->
        <div class="p-6" v-else-if="step === 1">
            <div class="grid grid-cols-3 gap-2 mb-4">
                <div
                    v-for="stat in stats"
                    :key="stat.key"
                    class="bg-minplan-lightbeige rounded p-3"
                >
                    <p class="text-xs text-gray-500">{{ stat.label }}</p>
                    <p class="text-lg font-semibold text-gray-900">{{ stat.count }}</p>
                </div>
            </div>

            <p v-if="completedCount" class="text-xs text-gray-500 mb-2">
                {{
                    $t(
                        "templateTransfer.completedCount",
                        { count: completedCount },
                        completedCount
                    )
                }}
            </p>

            <!-- Variante A: eine Karte je Vorlagenänderung. Der Entscheid gilt für alle
                 Normalfälle; Ausnahmen und Gebäude klappen auf. -->
            <div class="space-y-2 max-h-96 overflow-y-auto pr-1">
                <div
                    v-for="group in groups"
                    :key="group.id"
                    class="border rounded px-3 py-2"
                >
                    <div class="flex items-center gap-2 flex-wrap">
                        <p class="text-sm font-semibold text-gray-900 mr-auto">
                            {{ group.label }}
                        </p>
                        <!-- gilt für die Normalfälle; gibt es nur Ausnahmen, für alle Ausnahmen -->
                        <decision-toggle
                            :model-value="
                                decisionOf(
                                    group.normal.length ? group.normal : group.exceptions
                                )
                            "
                            :label="group.label"
                            @update:model-value="
                                setDecision(
                                    group.normal.length ? group.normal : group.exceptions,
                                    $event
                                )
                            "
                        />
                    </div>

                    <!-- Die Etiketten sind zugleich die Aufklapper: Normalfälle → Gebäude/Wohnung,
                         jede Ausnahme-Art → ihre Wohnungen. -->
                    <div class="flex items-center gap-2 flex-wrap mt-1 text-xs">
                        <button
                            v-if="group.normal.length"
                            type="button"
                            :class="chipClass('apply', isOpen(group.id, 'buildings'))"
                            :aria-expanded="isOpen(group.id, 'buildings')"
                            @click="toggle(group.id, 'buildings')"
                        >
                            {{
                                $t(
                                    "templateTransfer.apartmentCount",
                                    { count: group.normal.length },
                                    group.normal.length
                                )
                            }}
                            <ChevronDownIcon
                                v-if="isOpen(group.id, 'buildings')"
                                class="h-3 w-3 ml-1"
                                aria-hidden="true"
                            />
                            <ChevronRightIcon
                                v-else
                                class="h-3 w-3 ml-1"
                                aria-hidden="true"
                            />
                        </button>
                        <button
                            v-for="type in group.exceptionTypes"
                            :key="type.key"
                            type="button"
                            :class="
                                chipClass(type.status, isOpen(group.id, 't:' + type.key))
                            "
                            :aria-expanded="isOpen(group.id, 't:' + type.key)"
                            @click="toggle(group.id, 't:' + type.key)"
                        >
                            {{ type.label }}
                            <ChevronDownIcon
                                v-if="isOpen(group.id, 't:' + type.key)"
                                class="h-3 w-3 ml-1"
                                aria-hidden="true"
                            />
                            <ChevronRightIcon
                                v-else
                                class="h-3 w-3 ml-1"
                                aria-hidden="true"
                            />
                        </button>
                        <span
                            v-if="group.deleted"
                            :class="['px-2 py-0.5 rounded border', tagClass('deleted')]"
                        >
                            {{
                                $t(
                                    "templateTransfer.tag.deleted",
                                    { count: group.deleted },
                                    group.deleted
                                )
                            }}
                        </span>
                    </div>

                    <decision-tree
                        v-if="isOpen(group.id, 'buildings')"
                        class="mt-2 ml-2 pl-3 border-l-2 border-minplan-lightwhite"
                        :entries="group.normal"
                        :decisions="decisions"
                        :choices-for="choicesFor"
                        @set="setDecision"
                    />

                    <template
                        v-for="type in group.exceptionTypes"
                        :key="'open-' + type.key"
                    >
                        <!-- Staging-Runde 2: der Grund steht einmal oben als kurzer Satz, je
                             Wohnung nur, was sie unterscheidet; «Alle N» erst ab zwei Wohnungen -->
                        <div
                            v-if="isOpen(group.id, 't:' + type.key)"
                            class="mt-3 ml-2 pl-3 border-l-2 border-minplan-lightwhite text-xs"
                        >
                            <p
                                v-for="why in whyKeys(type)"
                                :key="why"
                                class="text-gray-600 leading-relaxed mt-1.5 mb-3.5"
                                data-test="exception-why"
                            >
                                <span class="font-semibold text-gray-900"
                                    >{{ $t(`templateTransfer.why.${why}.label`) }}:</span
                                >
                                {{ $t(`templateTransfer.why.${why}.text`) }}
                            </p>
                            <div
                                v-if="type.entries.length > 1"
                                class="flex items-center gap-2 pb-2 mb-2 border-b border-gray-100"
                                data-test="exception-all"
                            >
                                <span class="font-semibold text-gray-500 mr-auto">{{
                                    $t("templateTransfer.all", {
                                        count: type.entries.length,
                                    })
                                }}</span>
                                <decision-toggle
                                    :model-value="decisionOf(type.entries)"
                                    :choices="
                                        type.key === 'blocked'
                                            ? ['keep', 'discard']
                                            : ['apply', 'keep', 'discard']
                                    "
                                    :label="type.label"
                                    @update:model-value="
                                        setDecision(type.entries, $event)
                                    "
                                />
                            </div>
                            <decision-tree
                                :entries="type.entries"
                                :decisions="decisions"
                                :choices-for="choicesFor"
                                :detail-for="exceptionDetail"
                                aligned
                                @set="setDecision"
                            />
                        </div>
                    </template>
                </div>
                <p v-if="!groups.length" class="text-sm text-gray-500">
                    {{ $t("templateTransfer.nothing") }}
                </p>
            </div>

            <div
                class="rounded-md bg-yellow-50 border border-yellow-200 p-3 mt-4 text-sm text-yellow-800 space-y-1"
            >
                <p v-if="isPartial">{{ $t("templateTransfer.partialHint") }}</p>
                <p>{{ $t("templateTransfer.warnBuyers") }}</p>
                <p>{{ $t("templateTransfer.warnIrreversible") }}</p>
                <p v-if="countWithReason('accepted_offer')">
                    {{
                        $t(
                            "templateTransfer.warnOffers",
                            { count: countWithReason("accepted_offer") },
                            countWithReason("accepted_offer")
                        )
                    }}
                </p>
                <p v-if="countWithReason('in_progress')">
                    {{
                        $t(
                            "templateTransfer.warnInProgress",
                            { count: countWithReason("in_progress") },
                            countWithReason("in_progress")
                        )
                    }}
                </p>
            </div>
        </div>

        <!-- Schritt 2: Bericht (Staging-Runde 2). Zusammenfassung ohne Nullen, darunter je
             Ergebnis ein Abschnitt; je Änderung der Name wie auf der Karte der Vorschau, bei
             «Später» der Grund als Satz, je Wohnung nur, was sie unterscheidet. Die Abschnitte
             scrollen für sich (E1), damit die Knöpfe unten immer sichtbar bleiben. -->
        <div class="p-6" v-else-if="step === 2">
            <p class="text-sm text-gray-900 mb-4" data-test="report-summary">
                {{ reportSummary }}
            </p>
            <div
                v-if="reportSections.length"
                class="max-h-96 overflow-y-auto pr-1 space-y-5"
                data-test="report-scroll"
            >
                <div
                    v-for="section in reportSections"
                    :key="section.key"
                    data-test="report-section"
                >
                    <p class="text-[13px] text-gray-600 mb-2">
                        <span class="font-bold text-gray-900"
                            >{{
                                $t(`templateTransfer.section.${section.key}.title`)
                            }}:</span
                        >
                        {{ $t(`templateTransfer.section.${section.key}.text`) }}
                    </p>
                    <div class="border rounded divide-y divide-gray-100">
                        <div
                            v-for="group in section.groups"
                            :key="group.id"
                            class="px-3 py-2.5"
                        >
                            <p
                                v-if="group.title"
                                class="text-sm font-medium text-gray-900"
                                data-test="report-group-title"
                            >
                                {{ group.title }}
                            </p>
                            <!-- der Grund nur bei «Später»: dort sagt er, was zu tun ist -->
                            <p
                                v-if="section.key === 'kept' && group.why"
                                class="text-xs text-gray-600 mt-1"
                                data-test="report-why"
                            >
                                <span class="font-semibold text-gray-900"
                                    >{{
                                        $t(`templateTransfer.why.${group.why}.label`)
                                    }}:</span
                                >
                                {{ $t(`templateTransfer.why.${group.why}.text`) }}
                            </p>
                            <!-- mit Angabe: Wohnung und Angabe in zwei bündigen Spalten -->
                            <div
                                v-if="group.rows.some((row) => row.detail)"
                                class="grid grid-cols-[11.25rem_minmax(0,1fr)] gap-x-3 gap-y-1 mt-2 text-xs"
                            >
                                <template v-for="(row, index) in group.rows" :key="index">
                                    <span
                                        class="font-semibold text-gray-900 truncate"
                                        :title="row.name"
                                        >{{ row.name }}</span
                                    >
                                    <span
                                        class="text-gray-500 truncate"
                                        :title="row.detail || undefined"
                                        >{{ row.detail }}</span
                                    >
                                </template>
                            </div>
                            <!-- ohne Angabe: bis drei Wohnungen in der Zeile, sonst zum Aufklappen -->
                            <p
                                v-else-if="group.rows.length <= 3"
                                class="text-xs text-gray-500 mt-1.5"
                            >
                                {{ group.rows.map((row) => row.name).join(", ") }}
                            </p>
                            <template v-else>
                                <button
                                    type="button"
                                    class="inline-flex items-center mt-1.5 px-2 py-0.5 rounded border border-gray-300 bg-white text-xs text-gray-700 cursor-pointer hover:shadow-sm hover:brightness-95 focus:outline-none focus:ring-2 focus:ring-minplan-blue"
                                    :aria-expanded="isOpen(group.id, 'list')"
                                    @click="toggle(group.id, 'list')"
                                >
                                    {{
                                        $t(
                                            "templateTransfer.apartmentCount",
                                            { count: group.rows.length },
                                            group.rows.length
                                        )
                                    }}
                                    <ChevronDownIcon
                                        v-if="isOpen(group.id, 'list')"
                                        class="h-3 w-3 ml-1"
                                        aria-hidden="true"
                                    />
                                    <ChevronRightIcon
                                        v-else
                                        class="h-3 w-3 ml-1"
                                        aria-hidden="true"
                                    />
                                </button>
                                <template v-if="isOpen(group.id, 'list')">
                                    <div
                                        v-for="(building, index) in byBuilding(
                                            group.rows
                                        )"
                                        :key="index"
                                        class="mt-2 text-xs"
                                        data-test="report-building"
                                    >
                                        <p class="font-semibold text-gray-900 mb-0.5">
                                            {{
                                                building.name ||
                                                $t("templateTransfer.noBuilding")
                                            }}
                                            · {{ building.names.length }}
                                        </p>
                                        <div
                                            class="columns-2 sm:columns-3 gap-x-4 text-gray-500"
                                        >
                                            <span
                                                v-for="(name, position) in building.names"
                                                :key="position"
                                                class="block truncate"
                                                :title="name"
                                                >{{ name }}</span
                                            >
                                        </div>
                                    </div>
                                </template>
                            </template>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="flex justify-end items-center my-4 mx-6 gap-3">
            <template v-if="step === 1">
                <label class="flex items-center mr-auto text-sm text-gray-700">
                    <input type="checkbox" class="rounded mr-2" v-model="notify" />
                    {{ $t("templateTransfer.notify") }}
                </label>
                <button type="button" class="text-sm font-semibold" @click="close">
                    {{ $t("actions.cancel") }}
                </button>
                <primary-button
                    type="button"
                    :disabled="submitting || !groups.length"
                    :class="{ 'opacity-25': submitting }"
                    @click="submit"
                >
                    {{ $t("templateTransfer.transfer") }}
                </primary-button>
            </template>
            <template v-else-if="step === 2">
                <secondary-button v-if="hasRetryable" type="button" @click="retry">
                    {{ $t("templateTransfer.retry") }}
                </secondary-button>
                <primary-button type="button" @click="finish">
                    {{ $t("templateTransfer.close") }}
                </primary-button>
            </template>
        </div>
    </modal>
</template>

<script>
import { defineComponent } from "vue";
import axios from "axios";
import Modal from "@/Jetstream/Modal";
import PrimaryButton from "@/Jetstream/PrimaryButton";
import SecondaryButton from "@/Jetstream/SecondaryButton";
import PageTitle from "@/Shared/Components/PageTitle";
import DecisionToggle from "@/Pages/Projects/Components/DecisionToggle";
import DecisionTree from "@/Pages/Projects/Components/DecisionTree";
import { ChevronDownIcon, ChevronRightIcon } from "@heroicons/vue/outline";

// Dialog in zwei Schritten (Vorschau, Bericht), Plan 2026-10-08-vorlage-auf-wohnungen, Task 10.
// Eine vorgeschaltete Wohnungsauswahl gab es zuerst; sie ist entfallen (Peter, 04.10.2026),
// weil in der Vorschau ohnehin je Änderung, je Gebäude und je Ausnahme entschieden wird.
// Aufbau und Optik bewusst wie `Modals/DistributeDocument.vue`: gleicher Kopf, gleicher
// Bericht am Ende. Die Vorschau wird einmal für alle Wohnungen geladen.
export default defineComponent({
    props: {
        show: Boolean,
        projectUuid: String,
        apartments: Array,
    },
    emits: ["close", "done"],
    components: {
        Modal,
        PrimaryButton,
        SecondaryButton,
        PageTitle,
        DecisionToggle,
        DecisionTree,
        ChevronDownIcon,
        ChevronRightIcon,
    },
    data() {
        return {
            step: 1,
            plans: [],
            decisions: {},
            open: {},
            notify: false,
            report: null,
            loading: false,
            submitting: false,
            error: false,
        };
    },
    watch: {
        show(visible) {
            if (visible) this.start();
        },
    },
    mounted() {
        if (this.show) this.start();
    },
    computed: {
        stepLabel() {
            return [
                this.$t("templateTransfer.stepPreview"),
                this.$t("templateTransfer.stepReport"),
            ][this.step - 1];
        },
        openApartments() {
            const open = this.plans
                .filter((plan) => !plan.completed && this.pendingOf(plan).length)
                .map((plan) => plan.uuid);
            return this.apartments.filter((apartment) => open.includes(apartment.uuid));
        },
        // Nicht jede offene Änderung geht an jede Wohnung: dann gilt der Hinweis zu neuen Wohnungen.
        isPartial() {
            return this.groups.some((group) =>
                group.normal.some((entry) => this.decisions[entry.item.key] !== "apply")
            );
        },
        // alle Wohnungen mit offenen Punkten; abgeschlossene nur für den Hinweis oben
        selectedPlans() {
            return this.plans.filter(
                (plan) => plan.completed || this.pendingOf(plan).length
            );
        },
        completedCount() {
            return this.selectedPlans.filter((plan) => plan.completed).length;
        },
        // Punkte über alle gewählten Wohnungen nach Vorlagenänderung gruppiert
        // (Schlüssel ohne Wohnung: «Projektthema|Feld»).
        groups() {
            const groups = new Map();
            this.selectedPlans
                .filter((plan) => !plan.completed)
                .forEach((plan) =>
                    plan.items
                        .filter((item) => item.status !== "sync")
                        .forEach((item) => {
                            const id = item.key.slice(item.key.indexOf("|") + 1);
                            if (!groups.has(id)) {
                                groups.set(id, {
                                    id,
                                    label: this.describeChange(item),
                                    normal: [],
                                    exceptions: [],
                                    deleted: 0,
                                });
                            }
                            const group = groups.get(id);
                            if (item.status === "apply")
                                group.normal.push({ plan, item });
                            else if (item.status === "deleted") group.deleted++;
                            else group.exceptions.push({ plan, item });
                        })
                );

            return [...groups.values()]
                .filter((group) => group.normal.length || group.exceptions.length)
                .map((group) => {
                    const count = (fn) => group.exceptions.filter(fn).length;
                    const tags = [
                        {
                            key: "sameName",
                            status: "individual",
                            count: count((e) => e.item.reason === "same_name"),
                        },
                        {
                            key: "individual",
                            status: "individual",
                            count: count(
                                (e) =>
                                    e.item.status === "individual" &&
                                    e.item.reason !== "same_name"
                            ),
                        },
                        {
                            key: "offer",
                            status: "confirm",
                            count: count((e) => e.item.reason === "accepted_offer"),
                        },
                        {
                            key: "progress",
                            status: "confirm",
                            count: count((e) => e.item.reason === "in_progress"),
                        },
                        {
                            key: "blocked",
                            status: "blocked",
                            count: count((e) => e.item.status === "blocked"),
                        },
                        { key: "deleted", status: "deleted", count: group.deleted },
                    ]
                        .filter((tag) => tag.count)
                        .map((tag) => ({
                            ...tag,
                            label: this.$t(
                                `templateTransfer.tag.${tag.key}`,
                                { count: tag.count },
                                tag.count
                            ),
                        }));

                    const types = [
                        { key: "sameName", match: (e) => e.item.reason === "same_name" },
                        {
                            key: "individual",
                            match: (e) =>
                                e.item.status === "individual" &&
                                e.item.reason !== "same_name",
                        },
                        {
                            key: "offer",
                            match: (e) => e.item.reason === "accepted_offer",
                        },
                        {
                            key: "progress",
                            match: (e) => e.item.reason === "in_progress",
                        },
                        { key: "blocked", match: (e) => e.item.status === "blocked" },
                    ]
                        .map((type) => ({
                            ...type,
                            entries: group.exceptions.filter(type.match),
                        }))
                        .filter((type) => type.entries.length)
                        .map((type) => ({
                            key: type.key,
                            entries: type.entries,
                            status: {
                                sameName: "individual",
                                individual: "individual",
                                offer: "confirm",
                                progress: "confirm",
                                blocked: "blocked",
                            }[type.key],
                            label: this.$t(
                                `templateTransfer.tag.${type.key}`,
                                { count: type.entries.length },
                                type.entries.length
                            ),
                        }));
                    return { ...group, tags, exceptionTypes: types };
                });
        },
        stats() {
            const items = this.selectedPlans
                .filter((plan) => !plan.completed)
                .flatMap((plan) => this.visibleItems(plan))
                .filter((item) => item.status !== "deleted");
            const count = (decision) =>
                items.filter((item) => this.decisions[item.key] === decision).length;
            return [
                {
                    key: "apply",
                    label: this.$t("templateTransfer.statApply"),
                    count: count("apply"),
                },
                {
                    key: "keep",
                    label: this.$t("templateTransfer.statKeep"),
                    count: count("keep"),
                },
                {
                    key: "discard",
                    label: this.$t("templateTransfer.statDiscard"),
                    count: count("discard"),
                },
            ];
        },
        // E3: je Abschnitt die Zahl seiner Zeilen. Das Backend zählt in `kept` auch die
        // entschiedenen Punkte abgeschlossener Wohnungen mit; die Zusammenfassung zählt deshalb
        // die Berichtszeilen selbst, damit sie genau den Abschnitten darunter entspricht.
        reportCounts() {
            const counts = { kept: 0, discarded: 0, completed: 0 };
            (this.report?.notApplied ?? []).forEach((line) => {
                const key = this.sectionOf(line);
                if (key in counts) counts[key]++;
            });
            return counts;
        },
        // nur Teile, die nicht null sind: «4 Änderungen in 2 Wohnungen übertragen, 2 nicht übertragen.»
        reportSummary() {
            const report = this.report;
            const counts = this.reportCounts;
            const parts = [
                report.applied
                    ? `${this.$t(
                          "templateTransfer.summary.changes",
                          { count: report.applied },
                          report.applied
                      )} ${this.$t(
                          "templateTransfer.summary.applied",
                          { count: report.apartments },
                          report.apartments
                      )}`
                    : this.$t("templateTransfer.summary.nothing"),
            ];
            ["kept", "discarded", "completed"]
                .filter((key) => counts[key])
                .forEach((key) =>
                    parts.push(
                        this.$t(
                            `templateTransfer.summary.${key}`,
                            { count: counts[key] },
                            counts[key]
                        )
                    )
                );
            const summary = `${parts.join(", ")}.`;
            return report.notified
                ? `${summary} ${this.$t(
                      "templateTransfer.summary.notified",
                      { count: report.notified },
                      report.notified
                  )}`
                : summary;
        },
        // je Ergebnis ein Abschnitt, darin je Änderung und Grund eine Gruppe; abgeschlossene
        // Wohnungen haben keine Änderung und stehen für sich
        reportSections() {
            const sections = {
                kept: new Map(),
                discarded: new Map(),
                completed: new Map(),
            };
            (this.report?.notApplied ?? []).forEach((line) => {
                const key = this.sectionOf(line);
                if (!sections[key]) return; // das Backend meldet nur «kept» und «discarded»
                // Auswahllisten tragen immer das Feld «sets»; die Liste selbst steht in
                // to (anhängen) bzw. from (entfernen) und trennt die Gruppen wie in der Vorschau
                const isSet = line.kind === "attach_set" || line.kind === "detach_set";
                const id = [
                    key,
                    line.topic,
                    line.field,
                    line.kind,
                    line.reason,
                    ...(isSet ? [line.to ?? line.from] : []),
                ].join("|");
                if (!sections[key].has(id)) {
                    sections[key].set(id, {
                        id,
                        title: line.topic ? this.describeChange(line) : null,
                        why: this.reportWhy(line.reason),
                        rows: [],
                    });
                }
                sections[key].get(id).rows.push({
                    name: line.apartment.trim(),
                    detail: this.exceptionDetail({ item: line }),
                    building: line.building ?? null,
                });
            });
            return Object.entries(sections)
                .filter(([, groups]) => groups.size)
                .map(([key, groups]) => ({ key, groups: [...groups.values()] }));
        },
        hasRetryable() {
            return this.report?.notApplied.some(
                (line) => line.outcome === "kept" && line.reason !== "completed"
            );
        },
    },
    methods: {
        start() {
            this.step = 1;
            this.notify = false;
            this.report = null;
            this.load();
        },
        load() {
            this.loading = true;
            this.error = false;
            const all = this.apartments.map((apartment) => apartment.uuid);
            return axios
                .post(
                    this.route("advisors.projects.templateTransfer.preview", {
                        project: this.projectUuid,
                    }),
                    {
                        apartments: all,
                    }
                )
                .then(({ data }) => {
                    this.plans = data.apartments;
                    this.initDecisions();
                })
                .catch(() => (this.error = true))
                .finally(() => (this.loading = false));
        },
        // Voreinstellungen wie im Manifest (apply → apply, individual → discard, sonst keep)
        initDecisions() {
            const decisions = {};
            this.plans.forEach((plan) =>
                plan.items
                    .filter((item) => !["sync", "deleted"].includes(item.status))
                    .forEach((item) => {
                        decisions[item.key] =
                            item.status === "apply"
                                ? "apply"
                                : item.status === "individual"
                                ? "discard"
                                : "keep";
                    })
            );
            this.decisions = decisions;
        },
        decisionOf(entries) {
            const values = [
                ...new Set(entries.map((entry) => this.decisions[entry.item.key])),
            ];
            return values.length === 1 ? values[0] : null;
        },
        // gesperrte Punkte können nicht übertragen werden: dort bleibt «Offen lassen»
        setDecision(entries, decision) {
            entries.forEach((entry) => {
                this.decisions[entry.item.key] = this.choicesFor(entry.item).includes(
                    decision
                )
                    ? decision
                    : "keep";
            });
        },
        toggle(id, part) {
            const key = `${id}#${part}`;
            this.open = { ...this.open, [key]: !this.open[key] };
        },
        isOpen(id, part) {
            return !!this.open[`${id}#${part}`];
        },
        choicesFor(item) {
            return item.status === "blocked"
                ? ["keep", "discard"]
                : ["apply", "keep", "discard"];
        },
        pendingOf(plan) {
            return plan.items.filter((item) =>
                ["apply", "individual", "confirm", "blocked"].includes(item.status)
            );
        },
        visibleItems(plan) {
            return plan.items.filter((item) => item.status !== "sync");
        },
        countWithReason(reason) {
            return this.selectedPlans.filter((plan) =>
                plan.items.some((item) => item.reason === reason)
            ).length;
        },
        chipClass(status, open) {
            return [
                "inline-flex items-center px-2 py-0.5 rounded border cursor-pointer",
                "hover:shadow-sm hover:brightness-95 focus:outline-none focus:ring-2 focus:ring-minplan-blue",
                this.tagClass(status),
                open ? "ring-1 ring-current" : "",
            ];
        },
        tagClass(status) {
            return {
                apply: "bg-green-50 border-green-200 text-green-800",
                individual: "bg-yellow-50 border-yellow-200 text-yellow-800",
                confirm: "bg-orange-50 border-orange-200 text-orange-800",
                blocked: "bg-red-50 border-red-200 text-red-800",
                deleted: "bg-gray-50 border-gray-200 text-gray-600",
            }[status];
        },
        fieldLabel(field) {
            const [name, locale] = field.split(".");
            const label = this.$t(`templateTransfer.field.${name}`);
            return locale ? `${label} (${locale})` : label;
        },
        value(value) {
            if (value === null || value === undefined || value === "")
                return this.$t("templateTransfer.empty");
            if (value === true) return this.$t("templateTransfer.yes");
            if (value === false) return this.$t("templateTransfer.no");
            return value;
        },
        describeChange(item) {
            if (item.kind === "update") {
                return `${item.topic} · ${this.fieldLabel(item.field)}: ${this.value(
                    item.to
                )}`;
            }
            return this.describe(item);
        },
        describe(item) {
            if (item.kind === "add" || item.kind === "remove") {
                return `${this.$t(`templateTransfer.kind.${item.kind}`)}: ${item.topic}`;
            }
            if (item.kind === "attach_set" || item.kind === "detach_set") {
                return `${item.topic} · ${this.$t(
                    `templateTransfer.kind.${item.kind}`
                )}: ${item.to ?? item.from}`;
            }
            return `${item.topic} · ${this.fieldLabel(item.field)}: ${this.value(
                item.from
            )} → ${this.value(item.to)}`;
        },
        // Satz oben je Ausnahme-Art. «Gesperrt» hat zwei Gründe; eine Änderung hat nur einen
        // (Thema entfernen: Einträge, Auswahlliste entfernen: Käuferwahl). Gelesen wird über
        // alle Wohnungen statt an der ersten: wäre die Invariante je verletzt, stünden beide
        // Sätze da statt eines falschen.
        whyKeys(type) {
            if (type.key !== "blocked") return [type.key];
            const reasons = new Set(type.entries.map((entry) => entry.item.reason));
            const keys = [];
            if ([...reasons].some((reason) => reason !== "buyer_selected"))
                keys.push("blockedData");
            if (reasons.has("buyer_selected")) keys.push("blockedSelection");
            return keys;
        },
        // je Wohnung nur, was sie unterscheidet; der Grund steht oben. Ohne Nullen, mit
        // Ein- und Mehrzahl («1 Dokument»).
        exceptionDetail(entry) {
            const item = entry.item;
            if (item.reason === "individual" && item.kind === "update") {
                return this.$t("templateTransfer.detail.ownValue", {
                    value: this.value(item.from),
                });
            }
            if (item.reason === "accepted_offer") {
                return this.$t("templateTransfer.detail.before", {
                    value: this.value(item.from),
                });
            }
            if (item.reason === "has_data") {
                const details = item.details ?? {};
                return ["modifications", "documents", "selections"]
                    .filter((key) => details[key] > 0)
                    .map((key) =>
                        this.$t(
                            `templateTransfer.detail.${key}`,
                            { count: details[key] },
                            details[key]
                        )
                    )
                    .join(", ");
            }
            return "";
        },
        sectionOf(line) {
            return line.reason === "completed" ? "completed" : line.outcome;
        },
        // Satz zum Grund im Bericht; ohne Grund (selbst «Später» gewählt) kein Satz
        reportWhy(reason) {
            return (
                {
                    individual: "individual",
                    same_name: "sameName",
                    accepted_offer: "offer",
                    in_progress: "progress",
                    has_data: "blockedData",
                    buyer_selected: "blockedSelection",
                    translation_missing: "translation",
                }[reason] ?? null
            );
        },
        // Gliederung nach Gebäude, Reihenfolge wie im Bericht; `null` heisst ohne Gebäude
        byBuilding(rows) {
            const buildings = new Map();
            rows.forEach((row) => {
                if (!buildings.has(row.building)) buildings.set(row.building, []);
                buildings.get(row.building).push(row.name);
            });
            return [...buildings].map(([name, names]) => ({ name, names }));
        },
        submit() {
            // Doppelklick: der zweite Klick erreicht den Server nicht (Review Task 8, W1)
            if (this.submitting) return;
            this.submitting = true;
            axios
                .post(
                    this.route("advisors.projects.templateTransfer.transfer", {
                        project: this.projectUuid,
                    }),
                    {
                        apartments: this.openApartments.map(
                            (apartment) => apartment.uuid
                        ),
                        notify: this.notify,
                        decisions: Object.fromEntries(
                            this.selectedPlans.flatMap((plan) =>
                                plan.items
                                    .filter((item) => item.key in this.decisions)
                                    .map((item) => [item.key, this.decisions[item.key]])
                            )
                        ),
                    }
                )
                .then(({ data }) => {
                    this.report = data;
                    this.step = 2;
                })
                .catch(() => (this.error = true))
                .finally(() => (this.submitting = false));
        },
        // Erneut übertragen: Vorschau neu laden, offen ist dann nur noch, was nicht übertragen wurde.
        retry() {
            this.report = null;
            // zurueck zur Vorschau; im Schritt 2 ohne Bericht bricht das Rendern ab
            this.step = 1;
            this.load();
        },
        close() {
            this.$emit("close");
        },
        finish() {
            this.$emit("done");
        },
    },
});
</script>
